import { Directive, ElementRef, inject, input, output } from '@angular/core';
import { fromEvent, map, merge, Observable, pairwise } from 'rxjs';

interface ITouchEvent {
  name: string;
  y: number;
  top: number;
}

const LOADER_ELEMENT = () => {
  const wrapper = document.createElement('div');
  wrapper.style.textAlign = 'center';
  wrapper.style.height = '0';
  wrapper.style.maxHeight = '60px';
  wrapper.style.overflow = 'hidden';
  const spinner = document.createElement('span');
  spinner.className = 'fal fa-spinner fa-spin';
  spinner.style.fontSize = '35px';
  spinner.style.color = 'black';
  spinner.style.marginBlock = '12.5px';
  wrapper.appendChild(spinner);
  return wrapper;
};

@Directive({
  selector: '[inflPullToRefresh]',
  standalone: true,
})
export class PullToRefreshDirective {
  private elementRef = inject(ElementRef);

  public touchDelta = input<number>(100);

  private loader: HTMLDivElement = LOADER_ELEMENT();

  private moveEvents: ITouchEvent[] = [];

  public pullToRefresh = output<void>();

  constructor() {
    this.elementRef.nativeElement.appendChild(this.loader);
  }

  private pulling$ = fromEvent(
    this.elementRef.nativeElement,
    'touchmove',
  ).subscribe({
    next: (event: TouchEvent) => {
      if (this.elementRef.nativeElement.scrollTop !== 0) {
        return;
      }
      this.moveEvents.push({
        name: 'touchmove',
        y: event.changedTouches[0].clientY,
        top: this.elementRef.nativeElement.scrollTop,
      });
      if (this.moveEvents.length > 5) {
        this.loader.style.height = `${(this.moveEvents.at(-1).y - this.moveEvents.at(0).y) / 5}px`;
      }
    },
  });

  private pullStart$: Observable<ITouchEvent> = fromEvent(
    this.elementRef.nativeElement,
    'touchstart',
  ).pipe(
    map((event: TouchEvent) => {
      return {
        name: 'touchstart',
        y: event.changedTouches[0].clientY,
        top: this.elementRef.nativeElement.scrollTop,
      };
    }),
  );

  private pullEnd$: Observable<ITouchEvent> = fromEvent(
    this.elementRef.nativeElement,
    'touchend',
  ).pipe(
    map((event: TouchEvent) => {
      this.resetLoader();
      return {
        name: 'touchend',
        y: event.changedTouches[0].clientY,
        top: this.elementRef.nativeElement.scrollTop,
      };
    }),
  );

  private evaluatePulling = merge(this.pullStart$, this.pullEnd$)
    .pipe(pairwise())
    .subscribe({
      next: (events) => {
        if (events[1].name !== 'touchend') {
          return;
        }
        if (
          events[1].y - events[0].y > this.touchDelta() &&
          events[0].top + events[1].top === 0
        ) {
          this.pullToRefresh.emit();
        }
      },
    });

  private resetLoader() {
    this.moveEvents = [];
    this.loader.style.height = '0';
  }
}
